import React, { useMemo } from 'react'
import {
  InfoWindow,
  Marker,
} from '@react-google-maps/api'
import { Clusterer } from '@react-google-maps/marker-clusterer'
import {
  getMamorioImageSrc,
  isDevice,
} from '../../helpers/CommonMamorioHelper'
import { MapMarkerPosition } from '../../helpers/mapMarkerHelper'
import {
  defaultItemIcon,
  deviceIcon,
} from '../../icons'
import './OverlappingMamoriosMarker.css'

type Props = {
  clusterer: Clusterer
  mamorios: IMamorio[]
  position: MapMarkerPosition
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  onMamorioSelect: (mamorio: IMamorio) => void | Promise<void>
  recentDetectedAt: (mamorio: IMamorio) => string
  infoWindowOptions: { pixelOffset: google.maps.Size | undefined }
  showMemo?: boolean
}

const countMarkerLabel = (count: number): string => (count > 99 ? '99+' : String(count))

const buildCountMarkerIcon = (count: number): google.maps.Icon => {
  const label = countMarkerLabel(count)
  const fontSize = label.length >= 3 ? 14 : 18
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
      <circle cx="26" cy="26" r="23" fill="#ffffff" stroke="#4ca7db" stroke-width="4"/>
      <circle cx="26" cy="26" r="17" fill="#4ca7db"/>
      <text x="26" y="31" text-anchor="middle" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="#ffffff">${label}</text>
    </svg>
  `
  const point = typeof window.google.maps.Point === 'function'
    ? new window.google.maps.Point(26, 26)
    : undefined

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(52, 52),
    ...(point ? { anchor: point } : {}),
  }
}

const getMarkerImageSrc = (mamorio: IMamorio): string => (
  isDevice(mamorio) ? deviceIcon : getMamorioImageSrc(mamorio, defaultItemIcon)
)

const getGroupTitle = (mamorio: IMamorio | undefined, count: number): string => {
  const targetName = mamorio && isDevice(mamorio) ? '端末' : 'MAMORIO'
  return `同じ位置の${targetName} ${count}件`
}

const OverlappingMamoriosMarker = ({
  clusterer,
  mamorios,
  position,
  isOpen,
  onOpen,
  onClose,
  onMamorioSelect,
  recentDetectedAt,
  infoWindowOptions,
  showMemo = false,
}: Props) => {
  const markerIcon = useMemo(() => buildCountMarkerIcon(mamorios.length), [mamorios.length])

  return (
    <Marker
      icon={markerIcon}
      position={position}
      clusterer={clusterer}
      title={getGroupTitle(mamorios[0], mamorios.length)}
      onClick={onOpen}
      onLoad={(marker) => {
        const markerWithCount = marker as google.maps.Marker & { mamorioCount?: number }
        markerWithCount.mamorioCount = mamorios.length
      }}
    >
      {isOpen && (
        <InfoWindow
          position={position}
          options={infoWindowOptions}
          onCloseClick={onClose}
        >
          <div className="overlapping-mamorios-window">
            <div className="overlapping-mamorios-window__title">
              {getGroupTitle(mamorios[0], mamorios.length)}
            </div>
            <div className="overlapping-mamorios-window__list">
              {mamorios.map((mamorio) => (
                <button
                  key={mamorio.id}
                  type="button"
                  className="overlapping-mamorios-window__item"
                  onClick={() => onMamorioSelect(mamorio)}
                >
                  <img
                    className="overlapping-mamorios-window__image"
                    src={getMarkerImageSrc(mamorio)}
                    alt=""
                    onError={(event) => {
                      const image = event.currentTarget
                      image.src = defaultItemIcon
                    }}
                  />
                  <span className="overlapping-mamorios-window__body">
                    <span className="overlapping-mamorios-window__name">
                      {mamorio.name}
                    </span>
                    {!isDevice(mamorio) && (
                      <span className="overlapping-mamorios-window__meta">
                        {`${mamorio.major}-${mamorio.minor}`}
                      </span>
                    )}
                    {showMemo && mamorio.memo && (
                      <span className="overlapping-mamorios-window__memo">
                        {mamorio.memo}
                      </span>
                    )}
                    <span className="overlapping-mamorios-window__detected-at">
                      {recentDetectedAt(mamorio)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </InfoWindow>
      )}
    </Marker>
  )
}

export default OverlappingMamoriosMarker
